/**
 * SkillManager
 * 
 * Manages skill selection and validation
 * - Validates skill availability (stamina, state)
 * - Calculates skill range
 * - Updates store and state machine
 */

import { useBattleStore } from '@/store/useBattleStore';
import { ControlStateMachine } from './ControlStateMachine';
import { PathCalculator } from './PathCalculator';
import { BattleStatus, BattleUnit, Skill, ControlState } from '@/core/types/battle.types';
import { PathRenderer } from '@/view/renderers/PathRenderer';
import { hasLineOfSight, idToCoords } from '@/core/utils/hex.utils';
import ManagerRegistry from '@/core/battle/ManagerRegistry';

export class SkillManager {
    
    constructor(
        private stateMachine: ControlStateMachine,
        private pathRenderer: PathRenderer
    ) {}
    
    // ═══════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════
    
    /**
     * Select skill for use
     * Validates and updates state if valid
     * 
     * @returns true if skill was selected, false if validation failed
     */
    selectSkill(skillId: string): boolean {
        // Validate
        const validation = this.validateSkillSelection(skillId);
        if (!validation.isValid) {
            console.log('[SkillManager] Cannot select skill:', validation.reason);
            return false;
        }
        this.pathRenderer.clear();
        useBattleStore.getState().setZocHexes(new Set<string>());
        const { unit, skill } = validation;

        // Calculate range
        const targetHexes = this.calculateSkillRange(unit!, skill!);
        
        // Update store
        useBattleStore.setState({
            selectedSkillId: skillId,
            targetHexes: targetHexes
        });
        
        // Update state machine
        if (this.stateMachine.is(ControlState.Idle)) {
            this.stateMachine.setState(ControlState.SkillTargeting);
        }
        
        console.log(`[SkillManager] Skill selected: ${skillId}, targets: ${targetHexes.length}`);
        return true;
    }
    
    /**
     * Deselect current skill
     */
    deselectSkill(): void {
        useBattleStore.setState({
            selectedSkillId: null,
            targetHexes: []
        });
        
        // Return to idle if in skill targeting mode
        if (this.stateMachine.is(ControlState.SkillTargeting)) {
            this.stateMachine.setState(ControlState.Idle);
        }
        
        console.log('[SkillManager] Skill deselected');
    }
    
    /**
     * Toggle skill (select if not selected, deselect if selected)
     */
    toggleSkill(skillId: string): boolean {
        const currentSkillId = useBattleStore.getState().selectedSkillId;
        
        if (currentSkillId === skillId) {
            this.deselectSkill();
            return false;
        }
        
        return this.selectSkill(skillId);
    }
    
    /**
     * Check if skill can be selected
     * Returns validation result without modifying state
     */
    canSelectSkill(skillId: string): { 
        canSelect: boolean; 
        reason?: string;
    } {
        const validation = this.validateSkillSelection(skillId);
        return {
            canSelect: validation.isValid,
            reason: validation.reason
        };
    }
    
    /**
     * Get currently selected skill
     */
    getSelectedSkill(): Skill | null {
        const { selectedUnitId, selectedSkillId } = useBattleStore.getState();
        if (!selectedUnitId || !selectedSkillId) return null;
        
        const unit = useBattleStore.getState().units.get(selectedUnitId);
        if (!unit) return null;
        
        return unit.skills.find(s => s.id === selectedSkillId) || null;
    }
    
    /**
     * Check if hex is valid target for current skill
     */
    isValidTarget(hexId: string): boolean {
        const { targetHexes } = useBattleStore.getState();
        return targetHexes.includes(hexId);
    }
    
    // ═══════════════════════════════════════════════════════════════════
    // VALIDATION
    // ═══════════════════════════════════════════════════════════════════
    
    private validateSkillSelection(skillId: string): SkillValidationResult {
        // 1. Check battle status
        const battleStatus = useBattleStore.getState().status;
        if (battleStatus !== BattleStatus.Playing) {
            return {
                isValid: false,
                reason: 'Battle is not active'
            };
        }
        
        // 2. Check control state
        if (!this.stateMachine.allowsSkillSelection()) {
            return {
                isValid: false,
                reason: `Cannot select skill in state: ${this.stateMachine.getState()}`
            };
        }
        
        // 3. Check unit selection
        const selectedUnitId = useBattleStore.getState().selectedUnitId;
        if (!selectedUnitId) {
            return {
                isValid: false,
                reason: 'No unit selected'
            };
        }
        
        // 4. Get unit
        const unit = useBattleStore.getState().units.get(selectedUnitId);
        if (!unit) {
            return {
                isValid: false,
                reason: 'Selected unit not found'
            };
        }
        
        // 5. Check if unit can act
        if (unit.isDead) {
            return {
                isValid: false,
                reason: 'Unit is dead'
            };
        }
        
        const state = useBattleStore.getState();
        const isUnitFinished = state.finishedPools.some(pool => 
            pool.unitIds.includes(selectedUnitId)
        );
        
        if (isUnitFinished) {
            return {
                isValid: false,
                reason: 'Unit has finished turn'
            };
        }
        
        // // 6. Check faction
        // const { activeFaction } = useBattleStore.getState();
        // if (unit.faction !== activeFaction) {
        //     return {
        //         isValid: false,
        //         reason: 'Not your faction is active'
        //     };
        // }
        
        // 7. Find skill
        const skill = unit.skills.find(s => s.id === skillId);
        if (!skill) {
            return {
                isValid: false,
                reason: 'Skill not found'
            };
        }
        
        // 8. Check stamina
        if (unit.currentStamina < skill.cost) {
            return {
                isValid: false,
                reason: `Not enough stamina (need ${skill.cost}, have ${unit.currentStamina})`
            };
        }
        
        // All checks passed
        return {
            isValid: true,
            unit,
            skill
        };
    }
    
    // ═══════════════════════════════════════════════════════════════════
    // RANGE CALCULATION
    // ═══════════════════════════════════════════════════════════════════
    
    // public calculateSkillRange(unit: BattleUnit, skill: Skill): string[] {
    //     const battleMap = useBattleStore.getState().battleMap;
        
    //     const center = { q: unit.q, r: unit.r };
    //     const range = skill.range;
        
    //     // Calculate hexes in range
    //     const rangeMap = PathCalculator.getRadius(
    //         center,
    //         range,
    //         battleMap,
    //         false // Don't check if blocked - show full range
    //     );
        
    //     return Array.from(rangeMap.keys());
    // }

    public calculateSkillRange(unit: BattleUnit, skill: Skill): string[] {
        const battleMap = useBattleStore.getState().battleMap;
        const center = { q: unit.q, r: unit.r };
        const registry = ManagerRegistry.getInstance().entityRegistry;

        
        // 1. Отримуємо всі гекси в радіусі (геометрично)
        // Якщо скіл ігнорує LOS (наприклад, мортира), залишаємо як було.
        const rangeMap = PathCalculator.getRadius(
            center,
            skill.range,
            battleMap,
            false, // checkBlocked: false, бо ми хочемо бачити ворогів за стінами, навіть якщо не можемо в них вцілити
            false
        );

        const allHexesInRange = Array.from(rangeMap.keys());

        // 2. Якщо скіл потребує LOS, фільтруємо
        if (skill.range > 1) {
            return allHexesInRange.filter(targetId => {
                const targetCoords: { q: number; r: number; } = registry.getHex(targetId)?.data ?? idToCoords(targetId);
                return hasLineOfSight(center, targetCoords, battleMap);
            });
        }

        return allHexesInRange;
    }
}

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface SkillValidationResult {
    isValid: boolean;
    reason?: string;
    unit?: BattleUnit;
    skill?: Skill;
}